import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  Tooltip,
} from "@material-ui/core";
import { toast } from "react-toastify";
import { updateUserPrivileges, fetchUserPrivileges } from "./UserService";
import { makeStyles } from "@material-ui/core/styles";
import { COLORS } from "../UserDashboard/dashboardConfig";

const USER_COL_WIDTH = 170;

const useStyles = makeStyles((theme) => ({
  paper: { width: "92vw", maxHeight: "92vh", borderRadius: 8 },
  title: {
    padding: theme.spacing(2.5, 3, 1),
    "& h2": { fontSize: 22, fontWeight: 800, color: COLORS.primaryDark },
  },
  hint: { fontSize: 13.5, color: COLORS.textMuted, marginTop: 2, fontWeight: 400 },
  content: { padding: theme.spacing(1, 3, 2) },
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
  },
  search: { width: 280 },
  changes: { color: COLORS.textMuted, fontSize: 13.5 },
  tableWrap: {
    maxHeight: "62vh",
    overflow: "auto",
    border: `1px solid ${COLORS.border}`,
    borderRadius: 8,
  },
  headCell: {
    background: "#f5f7fa",
    color: COLORS.primaryDark,
    fontWeight: 700,
    fontSize: 12.5,
    lineHeight: 1.25,
    textAlign: "center",
    verticalAlign: "bottom",
    minWidth: 96,
    maxWidth: 120,
    padding: theme.spacing(1, 0.5),
  },
  userHead: {
    left: 0,
    zIndex: 4,
    textAlign: "left",
    minWidth: USER_COL_WIDTH,
    paddingLeft: theme.spacing(2),
  },
  userCell: {
    position: "sticky",
    left: 0,
    zIndex: 2,
    background: "#fff",
    fontWeight: 600,
    minWidth: USER_COL_WIDTH,
    paddingLeft: theme.spacing(2),
    boxShadow: `inset -1px 0 0 ${COLORS.border}`,
  },
  checkCell: { textAlign: "center", padding: theme.spacing(0.25, 0.5) },
  changedRow: { "& > td": { backgroundColor: "#fff8e1" } },
  actions: { padding: theme.spacing(1.5, 3, 2) },
}));

const PrivilegesModal = ({ open, onClose, userIds }) => {
  const [privilegesData, setPrivilegesData] = useState([]);
  const [initialPrivilegesData, setInitialPrivilegesData] = useState([]);
  const classes = useStyles();
  const [search, setSearch] = useState("");

  // Mapowanie nagłówków interfejsu na nazwy kolumn w bazie danych
  const columnMapping = {
    "Kontrola wewnętrzna": "internalControl",
    Import: "import",
    Eksport: "export",
    "Wyprowadzenie z magazynu": "warehouseOut",
    "Wprowadzenie na magazyn": "warehouseIn",
    "Uszlachetnianie czynne": "activeRefinement",
    "Uszlachetnianie bierne": "passiveRefinement",
    "Powrotny przywóz": "returnImport",
    "Powrotny wywóz": "returnExport",
    "Import - wpis do rejestru": "importRegisterEntry",
    "Eksport - wpis do rejestru": "exportRegisterEntry",
    "Eksport PL + import": "exportImport",
    Tranzyt: "tranzyt",
    "Odprawa czasowa import": "timeImport",
    "Odprawa czasowa eksport": "timeEksport",
  };

  const headers = Object.keys(columnMapping); // Lista nagłówków interfejsu

  const loadPrivileges = async () => {
    try {
      const privileges = await fetchUserPrivileges();

      // Sortujemy użytkowników alfabetycznie według `username`
      privileges.sort((a, b) => a.username.localeCompare(b.username));

      setPrivilegesData(privileges);
      setInitialPrivilegesData(JSON.parse(JSON.stringify(privileges)));
    } catch (error) {
      console.error("Error fetching privileges:", error);
      toast.error("Błąd podczas pobierania przywilejów.");
    }
  };

  useEffect(() => {
    if (open) {
      loadPrivileges();
    }
  }, [open]);

  const handlePrivilegeChange = (userId, privilege) => {
    setPrivilegesData((prevState) =>
      prevState.map((user) =>
        user.id === userId
          ? {
              ...user,
              [columnMapping[privilege]]:
                user[columnMapping[privilege]] === 1 ? 0 : 1,
            }
          : user
      )
    );
  };

  const savePrivileges = async () => {
    try {
      const usersWithChanges = privilegesData.filter((user) => {
        const initialUser = initialPrivilegesData.find(
          (initial) => initial.id === user.id
        );

        if (!initialUser) {
          return false;
        }

        return Object.keys(columnMapping).some((key) => {
          const dbColumn = columnMapping[key];
          return user[dbColumn] !== initialUser[dbColumn];
        });
      });

      if (usersWithChanges.length === 0) {
        toast.info("Brak zmian do zapisania.");
        onClose();
        return;
      }

      await Promise.all(
        usersWithChanges.map(async (user) => {
          const updatedPrivileges = Object.keys(columnMapping).reduce(
            (acc, key) => {
              const dbColumn = columnMapping[key];
              if (user[dbColumn] !== undefined && user[dbColumn] !== null) {
                acc[dbColumn] = user[dbColumn];
              }
              return acc;
            },
            {}
          );

          if (Object.keys(updatedPrivileges).length > 0) {
            await updateUserPrivileges(user.id, updatedPrivileges);
          }
        })
      );
      toast.success("Przywileje zostały zaktualizowane.");
      onClose();
    } catch (error) {
      console.error("Błąd podczas aktualizacji przywilejów:", error);
      toast.error("Błąd podczas aktualizacji przywilejów.");
    }
  };

  const isChanged = (user) => {
    const initial = initialPrivilegesData.find((item) => item.id === user.id);
    return (
      !!initial &&
      headers.some((h) => user[columnMapping[h]] !== initial[columnMapping[h]])
    );
  };

  const visibleUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    return privilegesData.filter(
      (user) => !term || String(user.username).toLowerCase().includes(term)
    );
  }, [privilegesData, search]);

  const changedCount = privilegesData.filter(isChanged).length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      classes={{ paper: classes.paper }}
    >
      <DialogTitle disableTypography className={classes.title}>
        <h2 style={{ margin: 0 }}>Zarządzaj przywilejami</h2>
        <div className={classes.hint}>
          Zaznacz, do których typów odpraw użytkownik ma dostęp.
        </div>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <div className={classes.toolbar}>
          <TextField
            className={classes.search}
            size="small"
            variant="outlined"
            label="Szukaj użytkownika"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <span className={classes.changes}>
            {changedCount > 0
              ? `Niezapisane zmiany: ${changedCount} ${changedCount === 1 ? "użytkownik" : "użytkowników"}`
              : "Brak zmian"}
          </span>
        </div>
        <div className={classes.tableWrap}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell className={`${classes.headCell} ${classes.userHead}`}>
                  Użytkownik
                </TableCell>
                {headers.map((header) => (
                  <TableCell key={header} className={classes.headCell}>
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleUsers.map((user) => (
                <TableRow
                  key={user.id}
                  hover
                  className={isChanged(user) ? classes.changedRow : undefined}
                >
                  <TableCell className={classes.userCell}>{user.username}</TableCell>
                  {headers.map((header) => (
                    <TableCell key={header} className={classes.checkCell}>
                      <Tooltip title={user[columnMapping[header]] === 1 ? "Wyłącz" : "Włącz"}>
                        <Checkbox
                          color="primary"
                          size="small"
                          checked={user[columnMapping[header]] === 1}
                          onChange={() => handlePrivilegeChange(user.id, header)}
                        />
                      </Tooltip>
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button onClick={onClose}>Anuluj</Button>
        <Button variant="contained" color="primary" onClick={savePrivileges}>
          Zapisz
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrivilegesModal;
